/* Command Center — Wholesaling business operating system.
 *
 * A native re-implementation of Tracy's iSpeedToClose_Wholesaling_Command_Center
 * spreadsheet. The 4 tabs mirror the 4 non-instruction sheets:
 *   Dashboard · Lead Planner · Expenses · Monthly Tracker
 *
 * Where the spreadsheet asks the operator to type numbers manually, the app
 * auto-populates Leads Generated / Contracts Signed / Deals Closed from
 * live CRM data. First-time users can hit "Load Tracy's template" to seed
 * the exact defaults from the shared workbook.
 */
import { useEffect, useState } from "react";
import { LayoutDashboard, ListChecks, Wallet, CalendarRange, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DashboardTab from "./command_center/DashboardTab";
import LeadPlannerTab from "./command_center/LeadPlannerTab";
import ExpensesTab from "./command_center/ExpensesTab";
import MonthlyTrackerTab from "./command_center/MonthlyTrackerTab";

const TABS = [
  { id: "dashboard",       label: "Dashboard",        icon: LayoutDashboard },
  { id: "lead-planner",    label: "Lead Planner",     icon: ListChecks },
  { id: "expenses",        label: "Expenses",         icon: Wallet },
  { id: "monthly-tracker", label: "Monthly Tracker",  icon: CalendarRange },
];

export default function CommandCenter() {
  const [active, setActive] = useState("dashboard");
  const [hasData, setHasData] = useState(null); // null = checking

  useEffect(() => {
    // Fast probe — do we have any sources or expenses yet? Drives the
    // empty-state prompt to seed Tracy's template.
    (async () => {
      try {
        const [srcRes, expRes] = await Promise.all([
          api.get("/command-center/lead-sources"),
          api.get("/command-center/expenses"),
        ]);
        setHasData(
          (srcRes.data?.items?.length || 0) > 0 ||
          (expRes.data?.items?.length || 0) > 0,
        );
      } catch {
        setHasData(false);
      }
    })();
  }, []);

  const seedTemplate = async () => {
    try {
      const { data } = await api.post("/command-center/seed-defaults", {});
      if (data.already_had_data) {
        toast.info("You already have data — nothing changed.");
      } else {
        toast.success(`Loaded ${data.seeded_lead_sources} lead sources + ${data.seeded_expenses} expenses.`);
      }
      setHasData(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load template.");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="command-center-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Business planner</div>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1">
            Command Center
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Know your numbers cold. Project revenue, track expenses, and see whether every
            marketing dollar you spend is actually building a business.
          </p>
        </div>
        {hasData === false && (
          <button
            type="button"
            onClick={seedTemplate}
            className="btn-money"
            data-testid="command-center-seed-template"
          >
            <Sparkles size={14} /> Load Tracy&apos;s template
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-2 border-b border-[var(--line)] mb-6" data-testid="command-center-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-colors ${
                on
                  ? "bg-white text-[var(--ink)] border-b-2 border-[var(--primary)]"
                  : "text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-white/50"
              }`}
              data-testid={`command-center-tab-${t.id}`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {active === "dashboard" && <DashboardTab />}
      {active === "lead-planner" && <LeadPlannerTab />}
      {active === "expenses" && <ExpensesTab />}
      {active === "monthly-tracker" && <MonthlyTrackerTab />}
    </div>
  );
}
